<template>
  <div id="notice-editor" class="s-base">
    <v-card class="s-panel" max-width="1000">
      <v-card-title class="s-title pl-7">配信お知らせ情報入力画面</v-card-title>
      <v-container>
        <v-row>
          <v-col>
            <v-card-title>お知らせ内容</v-card-title>
            <v-textarea
              v-model="noticeInput"
              counter="1000"
              maxlength="1000"
              class="px-5 pb-5"
              outlined
              auto-grow
              no-resize
              placeholder="お知らせ内容を入力してください。"
            ></v-textarea>
          </v-col>
        </v-row>

        <v-row no-gutters justify="space-between">
          <v-col cols="auto" class="px-5 py-5">
            <v-btn width="100" color="primary" @click="goToPreviousPage()"
              >戻る</v-btn
            >
          </v-col>
          <v-col cols="auto" class="px-5 py-5">
            <v-btn width="100" color="secondary" @click="dialog.confirm = true">
              登録</v-btn
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card>
    <message-dialog
      :messageDialogFlag.sync="dialog.message"
      :dialogText="messageDialogText"
    />
    <confirmation-dialog
      v-if="dialog.confirm"
      :confirmationDialogFlag.sync="dialog.confirm"
      :noticeInput="noticeInput"
      @editNotice="editNotice()"
    />
    <request-error-dialog
      :requestErrorDialogFlag.sync="dialog.requestError"
      :httpStatus="httpStatus"
      :message="requestErrorDialogMessage"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { NETWORK_ERROR } from "../router";
import noticeModule from "../store/NoticeModule";
import MessageDialog from "./MessageDialog.vue";
import NtcEdtConfirmationDialog from "./NtcEdtConfirmationDialog.vue";
import RequestErrorDialog from "./RequestErrorDialog.vue";

@Component({
  components: {
    "message-dialog": MessageDialog,
    "confirmation-dialog": NtcEdtConfirmationDialog,
    "request-error-dialog": RequestErrorDialog,
  },
})
export default class NoticeEditor extends Vue {
  /** 配信お知らせ情報の入力値 */
  noticeInput = "";

  /** 画面遷移時にデータ読み込みのエラー判定 */
  mountedOk = false;

  /** ダイアログの表示フラグ */
  dialog = {
    message: false,
    confirm: false,
    requestError: false,
  };

  /** httpステータスコード */
  httpStatus = 500;

  /** メッセージダイアログのテキスト（固定値） */
  messageDialogText = {
    title: "登録完了",
    message: "登録が完了しました。",
  };

  /** エラーダイアログのテキスト */
  requestErrorDialogMessage = "";

  /**
   * DBの配信お知らせ情報を編集する。
   */
  async editNotice(): Promise<void> {
    const notice = {
      notice: this.noticeInput,
    };
    let result = false;
    try {
      this.dialog.confirm = false;
      result = await noticeModule.editNotice(notice);
    } catch (error) {
      this.requestErrorDialogMessage = "登録に失敗しました。";
      if (error instanceof Error) {
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
    }
    if (result === true) {
      await this.loadDataToStore();
      this.copyStoreData();
      this.dialog.message = true;
    }
  }

  /**
   * ひとつ前のページに遷移する。
   */
  goToPreviousPage(): void {
    this.$router.back();
  }

  /**
   * Storeのデータをコピーする。
   */
  copyStoreData(): void {
    // Storeのデータを書き換えないように、一度Json型に変換。
    this.noticeInput = JSON.parse(JSON.stringify(noticeModule.notice)).notice;
  }

  /**
   * データをDBからStoreに取り込む
   */
  async loadDataToStore(): Promise<void> {
    let result = false;
    try {
      result = await noticeModule.getNotice();
    } catch (error) {
      if (error instanceof Error) {
        this.requestErrorDialogMessage = "";
        if (error.message === "500") {
          // 500 Internal Server Error
          this.httpStatus = 500;
          this.dialog.requestError = true;
        } else if (error.message === "401") {
          // 401 Unauthorized
          this.httpStatus = 401;
          this.dialog.requestError = true;
        } else {
          // 運用上起こってはいけないエラー
          this.$router.push({ name: error.message });
        }
      } else {
        // 運用上起こってはいけないエラー
        this.$router.push({ name: NETWORK_ERROR });
      }
    }
    if (result === true) {
      this.httpStatus = 200;
      this.mountedOk = true;
    }
  }

  /**
   * 配信お知らせ情報入力画面に遷移した時に実行
   */
  async mounted() {
    await this.loadDataToStore();
    if (this.mountedOk === true) {
      this.copyStoreData();
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/scss/common.scss";
</style>
